import {
  DeviceStatus,
  DeviceType,
  ProgramType,
  type Device,
  type Program,
} from "../state/api.types";

export const programTypeToDeviceTypes = {
  [ProgramType.Battery]: [DeviceType.Battery],
  [ProgramType.Evcharging]: [DeviceType.Evse, DeviceType.Vehicle],
} as const satisfies Record<ProgramType, readonly DeviceType[]>;

type GetProgramDevicesProps = {
  programs: Program[];
  devices: Device[];
};

export const getProgramDevices = (props: GetProgramDevicesProps) => {
  const devicesByType = Map.groupBy(props.devices, (d) => d.device_type);

  const programDeviceMap = new Map<Program, Device[]>();

  for (const program of props.programs) {
    const deviceTypes = programTypeToDeviceTypes[program.program_type];
    const devices = deviceTypes.flatMap((d) => devicesByType.get(d) ?? []);
    programDeviceMap.set(program, devices);
  }

  return programDeviceMap;
};

/**
 * An inactive device is no longer serving its program, so the program reads as
 * having no device rather than a device with a problem.
 */
export const getIsMissing = (device: Device | undefined | null): boolean => {
  return !device || device.status === DeviceStatus.Inactive;
};

/** The devices that still serve a program; inactive ones count as missing. */
export const getServingDevices = (devices: Device[]): Device[] => {
  return devices.filter((device) => !getIsMissing(device));
};

export const getNeedsAttention = (
  device: Device | undefined | null,
): boolean => {
  return device?.status === DeviceStatus.NeedsAttention;
};

/**
 * Worst first. A program shows one icon however many devices serve it, so the
 * icon has to be the one someone needs to do something about. Keyed by status
 * rather than ordered, so a new status has to be ranked here to compile.
 */
const DEVICE_RANK: Record<DeviceStatus, number> = {
  [DeviceStatus.NeedsAttention]: 0,
  [DeviceStatus.Active]: 1,
  [DeviceStatus.Inactive]: 2,
};

/** The device speaking for a program: whichever most needs attention. */
export const findProgramDevice = (devices: Device[]): Device | undefined => {
  return devices.reduce<Device | undefined>(
    (worst, device) =>
      worst === undefined ||
      DEVICE_RANK[device.status] < DEVICE_RANK[worst.status]
        ? device
        : worst,
    undefined,
  );
};
